import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Loader2, TrendingUp, Award, Sparkles, Trophy, Milestone, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import AbiPassportTimeline from "./AbiPassportTimeline";

export default function AbiPassport() {
  const [p, setP] = useState(null);
  const [ai, setAi] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/abi/copilot/passport")
      .then(r => setP(r.data))
      .catch(() => toast.error("Erreur de chargement du passeport."))
      .finally(() => setLoading(false));
  }, []);
  // AI weekly narrative — fetched async, may take ~5-10s so we don't block
  useEffect(() => {
    api.get("/abi/copilot/wrapup/weekly/ai")
      .then(r => setAi(r.data))
      .catch(() => {});
  }, []);

  if (loading || !p) return (
    <div className="p-8 flex items-center gap-2" data-testid="abi-passport-loading">
      <Loader2 size={16} className="animate-spin" /> Chargement…
    </div>
  );

  const delta = p.maturity_delta_30d;
  const trendColor = delta >= 3 ? "#0B7A3B" : delta <= -3 ? "#B91C1C" : "#7A5B14";

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FBF6EA" }} data-testid="abi-passport-root">
      <TopBar />

      {/* Hero — the promise */}
      <div className="px-8 pt-8 pb-6"
            style={{ background: "linear-gradient(120deg,#0B2545 0%,#123B6E 60%,#1D5AA5 100%)" }}>
        <div className="max-w-6xl mx-auto text-ivory">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest opacity-80">
            <BadgeCheck size={14} /> ALTIORA · Passeport de Progression Professionnelle
          </div>
          <h1 className="text-3xl md:text-4xl font-serif mt-2 leading-tight text-white"
              data-testid="abi-passport-title">
            {p.promise_fr}
          </h1>
          <div className="mt-5 grid md:grid-cols-4 gap-4">
            {/* Level */}
            <div className="rounded-lg p-4"
                  style={{ backgroundColor: "rgba(197,168,128,0.20)" }}>
              <div className="text-[10px] uppercase tracking-widest text-white/70">Niveau actuel</div>
              <div className="font-serif text-2xl mt-1" style={{ color: "#F5D687" }}
                    data-testid="abi-passport-level">
                {p.level_fr}
              </div>
              <div className="text-[12px] text-white/85 mt-1">
                Profession · {p.profession_label_fr}
              </div>
            </div>
            {/* Maturity */}
            <div className="rounded-lg p-4"
                  style={{ backgroundColor: "rgba(255,255,255,0.10)" }}
                  data-testid="abi-passport-maturity">
              <div className="text-[10px] uppercase tracking-widest text-white/70">Maturité</div>
              <div className="font-serif text-3xl font-bold mt-1 text-white">
                {p.progress_percent.toFixed(0)}
                <span className="text-sm text-white/60">/100</span>
              </div>
              <div className="text-[12px] mt-1" style={{ color: delta >= 0 ? "#7EE0AA" : "#FCA5A5" }}>
                {delta >= 0 ? "+" : ""}{delta.toFixed(1)} pts / 30 j
              </div>
            </div>
            {/* Velocity */}
            <div className="rounded-lg p-4"
                  style={{ backgroundColor: "rgba(255,255,255,0.10)" }}
                  data-testid="abi-passport-velocity">
              <div className="text-[10px] uppercase tracking-widest text-white/70">Vélocité mensuelle</div>
              <div className="font-serif text-3xl font-bold mt-1 text-white">
                {p.velocity_month >= 0 ? "+" : ""}{p.velocity_month.toFixed(1)}
              </div>
              <div className="text-[12px] text-white/70 mt-1">pts / mois</div>
            </div>
            {/* Potential */}
            <div className="rounded-lg p-4"
                  style={{ backgroundColor: "rgba(255,255,255,0.10)" }}
                  data-testid="abi-passport-potential">
              <div className="text-[10px] uppercase tracking-widest text-white/70">Potentiel estimé</div>
              <div className="font-serif text-3xl font-bold mt-1 text-white">
                {p.potential_score.toFixed(0)}
                <span className="text-sm text-white/60">/100</span>
              </div>
              <div className="text-[12px] text-white/70 mt-1">projection à 3 mois</div>
            </div>
          </div>
        </div>
      </div>

      {/* Story paragraph */}
      <div className="max-w-6xl mx-auto px-8 pt-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border-l-4"
              style={{ borderLeftColor: trendColor }}
              data-testid="abi-passport-story">
          <div className="flex items-start gap-3">
            <Sparkles size={22} style={{ color: trendColor, marginTop: 2 }} />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold mb-1"
                    style={{ color: trendColor }}>
                Votre progression ce mois-ci
              </div>
              <p className="text-[15px] leading-relaxed text-gray-900">
                {p.story_paragraph_fr}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* AI weekly narrative — Claude Sonnet 4.5 */}
      {ai && ai.narrative && (
        <div className="max-w-6xl mx-auto px-8 pt-4">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden"
                data-testid="abi-passport-ai-narrative">
            <div className="p-5 border-b" style={{
                  background: "linear-gradient(120deg,#0B2545 0%,#123B6E 100%)",
                  borderColor: "#E5E7EB" }}>
              <div className="flex items-start gap-3 text-white">
                <div className="w-10 h-10 rounded flex items-center justify-center"
                      style={{ backgroundColor: "#C5A880", color: "#0B2545" }}>
                  <BadgeCheck size={20} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest opacity-80">
                    Rituel du vendredi · le mot de votre mentor
                  </div>
                  <h3 className="font-serif text-xl mt-0.5">
                    Ce que j'ai appris de vous cette semaine
                  </h3>
                </div>
                <span className="ml-auto text-[10px] uppercase tracking-widest opacity-60"
                        data-testid="abi-passport-ai-source">
                  {ai.source === "ai_claude_sonnet_4_5" ? "IA · Claude 4.5" : "Déterministe"}
                </span>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold mb-1"
                      style={{ color: "#0B2545" }}>
                  ① Ce que j'ai appris de vous
                </div>
                <p className="text-[14px] leading-relaxed text-slate-800">
                  {ai.narrative.learned_about_you_fr}
                </p>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold mb-1"
                      style={{ color: "#7A5B14" }}>
                  ② Focus de la semaine prochaine
                </div>
                <p className="text-[14px] leading-relaxed text-slate-800">
                  {ai.narrative.focus_next_week_fr}
                </p>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold mb-1"
                      style={{ color: "#0B7A3B" }}>
                  ③ Actions correctives concrètes
                </div>
                <ul className="space-y-1.5 text-[14px]">
                  {(ai.narrative.corrective_actions_fr || []).map((a, i) => (
                    <li key={i} className="flex gap-2 items-start p-2 rounded"
                          style={{ background: "#DCFCE7" }}
                          data-testid={`abi-passport-ai-action-${i}`}>
                      <span className="font-mono text-[11px] px-1.5 py-0.5 rounded flex-shrink-0"
                              style={{ background: "#0B7A3B", color: "#fff" }}>
                        {i + 1}
                      </span>
                      <span className="text-slate-800">{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Milestones */}
      {p.milestones && p.milestones.length > 0 && (
        <div className="max-w-6xl mx-auto px-8 pt-4">
          <div className="bg-white rounded-lg shadow-sm p-5" data-testid="abi-passport-milestones">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={18} style={{ color: "#C5A880" }} />
              <h3 className="font-serif text-lg" style={{ color: "#0B2545" }}>
                Célébrations · {p.milestones.length} palier(s) atteint(s)
              </h3>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {p.milestones.map((m, i) => (
                <div key={m.id} data-testid={`abi-passport-milestone-${i}`}
                      className="p-3 rounded flex items-start gap-2 text-[13px]"
                      style={{ background: m.is_new ? "#FEF3C7" : "#F3F4F6",
                                border: m.is_new ? "1px solid #C5A880" : "none" }}>
                  <span className="text-lg">{m.emoji}</span>
                  <div>
                    <div className="text-slate-900">{m.text_fr}</div>
                    {m.is_new && (
                      <div className="text-[10px] uppercase tracking-widest font-bold mt-1"
                            style={{ color: "#C5A880" }}>
                        ★ Nouveau
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Skills + habits split */}
      <div className="max-w-6xl mx-auto px-8 py-6 grid md:grid-cols-2 gap-4">
        <Section title="Compétences acquises" tone="#0B7A3B" accent="#DCFCE7"
                  items={p.acquired_skills_fr} empty="Aucune compétence n'atteint encore 75/100 — l'objectif du prochain palier."
                  tid="abi-passport-acquired" />
        <Section title="Compétences en développement" tone="#7A5B14" accent="#FBF6EA"
                  items={p.developing_skills_fr} empty="Les compétences en cours de consolidation apparaîtront ici."
                  tid="abi-passport-developing" />
        <Section title="Habitudes construites" tone="#0B2545" accent="#EEF4FF"
                  items={p.built_habits_fr} empty="Les habitudes confirmées sur 2 mois+ apparaîtront ici."
                  tid="abi-passport-habits-built" />
        <Section title="Frictions récurrentes" tone="#B91C1C" accent="#FEE2E2"
                  items={p.frictions_fr} empty="Aucune friction récurrente détectée — belle discipline."
                  tid="abi-passport-frictions" />
        <Section title="Vos points forts" tone="#7C3AED" accent="#EDE9FE"
                  items={p.strengths_fr} tid="abi-passport-strengths" />
        <Section title="Principaux freins actuels" tone="#B45309" accent="#FEF3C7"
                  items={p.main_blockers_fr} tid="abi-passport-blockers" />
      </div>

      {/* Progression Timeline — the objective proof of growth over time */}
      <div className="max-w-6xl mx-auto px-8 pb-6">
        <AbiPassportTimeline days={90} />
      </div>

      {/* Trajectory projections */}
      <div className="max-w-6xl mx-auto px-8 pb-10">
        <div className="bg-white rounded-lg shadow-sm p-6" data-testid="abi-passport-projections">
          <div className="flex items-center gap-2 mb-4">
            <Milestone size={18} style={{ color: "#0B2545" }} />
            <h3 className="font-serif text-xl" style={{ color: "#0B2545" }}>
              Votre trajectoire projetée
            </h3>
          </div>
          <div className="space-y-3">
            {["3_months_fr","1_year_fr","3_years_fr","5_years_fr"].map(k => (
              <div key={k} className="flex items-start gap-3 text-[14px]">
                <ChevronRight size={16} className="mt-0.5 flex-shrink-0" style={{ color: "#C5A880" }} />
                <div className="text-slate-800">{p.projections[k]}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t text-[12px] italic text-slate-600"
                style={{ borderTopColor: "#E5E7EB" }}>
            {p.projections.note_fr}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, items, empty, tone, accent, tid }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-5" data-testid={tid}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded flex items-center justify-center"
              style={{ backgroundColor: accent, color: tone }}>
          <Award size={12} />
        </div>
        <h3 className="font-serif text-lg" style={{ color: tone }}>{title}</h3>
      </div>
      {(items || []).length === 0 ? (
        <div className="text-[13px] italic text-slate-500">{empty || "—"}</div>
      ) : (
        <ul className="space-y-1 text-[13px]">
          {(items || []).map((it, i) => (
            <li key={i} className="text-slate-800">· {it}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
